/*
  API-Funktionen für den Schichtplan.
*/

import httpClient from "./httpClient";
import { activePeriodParams } from "./period";

/*
  Ein gutes Dutzend Aufrufe beantwortet der Server mit dem vollständigen Plan —
  auch Farbwechsel und Typenverwaltung. Sie alle brauchen das Zeitfenster,
  sonst käme als Antwort auf eine Kleinigkeit der laufende Monat statt der
  Monat, den man gerade ansieht. Was der Server ohne Fenster täte, steht in
  period.js.
*/
function planRequest(config = {}) {
  return { ...config, params: { ...activePeriodParams(), ...(config.params || {}) } };
}

export async function getShiftplanBootstrap() {
  const response = await httpClient.get("/api/shiftplan/bootstrap", planRequest());
  return response.data;
}

/*
  Schichtplan der Mitarbeitenden-Ansicht: gleiche Form wie der Bootstrap, aber
  nur Freigegebenes und ohne Planungsdaten. Der Planungs-Bootstrap ist für User
  gesperrt.
*/
export async function getEmployeeShiftplan() {
  const response = await httpClient.get("/api/web/employee/shiftplan", planRequest());
  return response.data;
}

// Kommentar eines eigenen, freigegebenen Eintrags (Web-Funktion
// page.shiftplan/employee_entry_comments). Leerer Text entfernt ihn.
export async function saveEmployeeEntryComment(entryId, comment) {
  const response = await httpClient.put(`/api/web/employee/shiftplan/entries/${entryId}/comment`, { comment });
  return response.data;
}

// Stunden der Mitarbeitenden-Ansicht: gleiche Rechnung wie getShiftplanWorkload, nur Freigegebenes.
export async function getEmployeeShiftplanWorkload({ startDate, endDate }) {
  const response = await httpClient.get("/api/web/employee/shiftplan/workload", {
    params: {
      start_date: startDate,
      end_date: endDate,
    },
  });
  return response.data;
}

/*
  Nur die zeitabhängigen Daten eines beliebigen Zeitraums, ohne Stammdaten.
  Für die Dialoge, die über den angezeigten Zeitraum hinausreichen.
*/
export async function getShiftplanEntries({ startDate, endDate, draftsOnly = false } = {}) {
  const params = draftsOnly
    ? { drafts_only: 1 }
    : { start_date: startDate, end_date: endDate };
  const response = await httpClient.get("/api/shiftplan/entries", { params });
  return response.data;
}

export async function getShiftplanWorkload({ startDate, endDate }) {
  const response = await httpClient.get("/api/shiftplan/workload", {
    params: {
      start_date: startDate,
      end_date: endDate,
    },
  });
  return response.data;
}

export async function getShiftplanDemand({ startDate, endDate }) {
  const response = await httpClient.get("/api/shiftplan/demand", {
    params: {
      start_date: startDate,
      end_date: endDate,
    },
  });
  return response.data;
}

export async function updateDisplayCalendar(displayCalendar) {
  const response = await httpClient.put("/api/shiftplan/settings/display-calendar", {
    display_calendar: displayCalendar,
  }, planRequest());
  return response.data;
}

export async function updateShiftDisplayMode(shiftDisplayMode) {
  const response = await httpClient.put("/api/shiftplan/settings/shift-display", {
    shift_display_mode: shiftDisplayMode,
  }, planRequest());
  return response.data;
}

export async function updateDepartmentColor({ id, colorHex }) {
  const response = await httpClient.put("/api/shiftplan/colors/department", {
    id,
    color_hex: colorHex,
  }, planRequest());
  return response.data;
}

export async function updateShiftColor({ id, colorHex }) {
  const response = await httpClient.put("/api/shiftplan/colors/shift", {
    id,
    color_hex: colorHex,
  }, planRequest());
  return response.data;
}

export async function saveSchedulerEvents(events) {
  const response = await httpClient.put("/api/shiftplan/events", {
    events: Array.isArray(events) ? events : [],
  }, planRequest());
  return response.data;
}

export async function saveDayComment(dateKey, comment) {
  const response = await httpClient.put("/api/shiftplan/day-comments", {
    date: dateKey,
    comment: String(comment || ""),
  }, planRequest());
  return response.data;
}

export async function sendReleaseNotifications(userIds) {
  const response = await httpClient.post("/api/shiftplan/release-notifications", {
    user_ids: (Array.isArray(userIds) ? userIds : []).map((id) => Number(id)),
  });
  return response.data;
}

// Nur die Feiertage eines Jahres – für die Liste in den Einstellungen.
export async function getPublicHolidays(year) {
  const response = await httpClient.get("/api/shiftplan/public-holidays", { params: { year: Number(year) } });
  return response.data;
}

export async function previewPublicHolidays({
  year,
  countryCode,
  postalCode,
  city,
  subdivisionCode,
  locationLabel,
}) {
  const response = await httpClient.post("/api/shiftplan/public-holidays/preview", {
    year: Number(year),
    country_code: String(countryCode || "").trim().toUpperCase(),
    postal_code: String(postalCode || "").trim(),
    city: String(city || "").trim(),
    subdivision_code: String(subdivisionCode || "").trim(),
    location_label: String(locationLabel || "").trim(),
  });
  return response.data;
}

export async function getPublicHolidayLocationSuggestions({ countryCode, postalCode, city }) {
  const response = await httpClient.post("/api/shiftplan/public-holidays/location-suggestions", {
    country_code: String(countryCode || "").trim().toUpperCase(),
    postal_code: String(postalCode || "").trim(),
    city: String(city || "").trim(),
  });
  return response.data;
}

export async function savePublicHolidayBatch({ items, fetchedScope }) {
  const response = await httpClient.post("/api/shiftplan/public-holidays/batch", {
    items,
    fetched_scope: fetchedScope || {},
  });
  return response.data;
}

export async function deletePublicHolidays(ids) {
  const response = await httpClient.post("/api/shiftplan/public-holidays/delete", {
    ids,
  });
  return response.data;
}

export async function updateEmployeeOrder(users) {
  const response = await httpClient.put("/api/shiftplan/employee-order", { users });
  return response.data;
}

export async function resetEmployeeOrder() {
  const response = await httpClient.post("/api/shiftplan/employee-order/reset", {});
  return response.data;
}

export async function updateDepartmentOrder(departments) {
  const response = await httpClient.put("/api/shiftplan/department-order", { departments });
  return response.data;
}

export async function resetDepartmentOrder() {
  const response = await httpClient.post("/api/shiftplan/department-order/reset", {});
  return response.data;
}

/*
  Saison-Sonderfall Wiesnshop: Der Server rendert das alte Excel-Format mit
  Bedarfsansicht (siehe backend_web/shiftplan_export_wiesnshop.py). Antwort
  ist die fertige xlsx-Datei als Blob.
*/
export async function downloadWiesnshopExcel({ startDate, endDate }) {
  const response = await httpClient.get("/api/shiftplan/export/wiesnshop-excel", {
    params: { start_date: startDate, end_date: endDate },
    responseType: "blob",
  });
  return response.data;
}
